import { z } from 'zod';

export const localeSchema = z.enum(['de', 'en']);
export type Locale = z.infer<typeof localeSchema>;

export const roleSchema = z.enum(['owner', 'member']);
export type Role = z.infer<typeof roleSchema>;

export const currentUserSchema = z.object({
  id: z.uuid(),
  displayName: z.string(),
  role: roleSchema,
  isAdmin: z.boolean(),
  locale: localeSchema,
  household: z.object({ id: z.uuid(), name: z.string() }),
});
export type CurrentUser = z.infer<typeof currentUserSchema>;

export const authStatusSchema = z.object({
  /** Null when signed out. */
  user: currentUserSchema.nullable(),
  /**
   * Signed in through a recovery code or login link: the session only allows adding a passkey,
   * and becomes a normal session once one is registered.
   */
  mustAddPasskey: z.boolean(),
});
export type AuthStatus = z.infer<typeof authStatusSchema>;

const name = z.string().trim().min(1).max(60);

/** First start: `/setup?token=…` creates the household and its owner. */
export const setupRequestSchema = z.object({
  token: z.string().min(1).max(200),
  householdName: name,
  displayName: name,
  locale: localeSchema,
});
export type SetupRequest = z.infer<typeof setupRequestSchema>;

export const setupStatusSchema = z.object({ available: z.boolean() });
export type SetupStatus = z.infer<typeof setupStatusSchema>;

/** Returned once when an account is created: the recovery codes are never shown again. */
export const accountCreatedSchema = z.object({ recoveryCodes: z.array(z.string()) });
export type AccountCreated = z.infer<typeof accountCreatedSchema>;

export const recoverRequestSchema = z.object({ code: z.string().trim().min(1).max(40) });
export const loginLinkRequestSchema = z.object({ token: z.string().min(1).max(200) });

const passkeyLabel = z.string().trim().min(1).max(60);

export const passkeySchema = z.object({
  id: z.uuid(),
  label: z.string().nullable(),
  deviceType: z.string().nullable(),
  backedUp: z.boolean(),
  createdAt: z.iso.datetime(),
  lastUsedAt: z.iso.datetime().nullable(),
});
export type Passkey = z.infer<typeof passkeySchema>;

export const passkeysSchema = z.object({
  passkeys: z.array(passkeySchema),
  /** Unused recovery codes left. */
  recoveryCodesLeft: z.number().int().nonnegative(),
});
export type Passkeys = z.infer<typeof passkeysSchema>;

export const addPasskeyRequestSchema = z.object({ label: passkeyLabel.nullable() });
export const renamePasskeyRequestSchema = z.object({ label: passkeyLabel });
